import { Button, Modal } from '@orbind/ui'
import { useEffect, useState } from 'react'
import { apiOrigin } from '../../lib/api.ts'
import { isDesktop, NOME_DO_SISTEMA, sistemaOperacional, urlDeDownload } from '../../lib/platform.ts'
import { useSession } from '../../lib/session.tsx'
import {
  deveOferecerODesktop,
  guardarDispensaDoDesktop,
  jaDispensouODesktop,
} from './convite-desktop.ts'
import './convite-desktop.css'

/**
 * Convite para instalar o Orbind Desktop (SPEC-20260925-1628).
 *
 * Aparece no login de quem NUNCA entrou pelo app instalado. O navegador não
 * enxerga a máquina, então quem responde "tem instalado?" é o servidor: o
 * canal de presença informa a plataforma e carimba `desktopSeenAt` na primeira
 * sessão vinda do desktop (ver modules/presence/routes.ts).
 *
 * Quatro portas fechadas antes de aparecer, nesta ordem:
 * 1. já está rodando no app instalado — não há o que oferecer;
 * 2. a pessoa já entrou pelo desktop alguma vez — tem instalado;
 * 3. dispensou o convite nesta sessão — "agora não" vale até o próximo login;
 * 4. não existe instalador publicado para o sistema dela — oferecer um download
 *    que não existe é pior que não oferecer. É isso que segura o convite no
 *    macOS (sem build) e no celular.
 */

type Release = { platform: string; version: string; sizeBytes: number }

export function ConviteParaODesktop() {
  const session = useSession()
  const sistema = sistemaOperacional()

  // As três primeiras portas não dependem de rede: decide antes de pedir
  // qualquer coisa ao servidor. A quarta (tem instalador?) vem do efeito.
  const podeConsultar = deveOferecerODesktop({
    noAppInstalado: isDesktop(),
    usouDesktop: session.usouDesktop === true,
    dispensou: jaDispensouODesktop(session.id),
    temInstalador: true,
  })

  const [release, setRelease] = useState<Release | null>(null)
  const [aberto, setAberto] = useState(false)

  useEffect(() => {
    if (!podeConsultar) return
    let vivo = true
    fetch(`${apiOrigin()}/download/releases`)
      .then((r) => (r.ok ? (r.json() as Promise<{ releases: Release[] }>) : null))
      .then((d) => {
        if (!vivo || !d) return
        // quarta porta: só abre se HÁ instalador para o sistema desta pessoa
        const meu = d.releases.find((r) => r.platform === sistema) ?? null
        if (meu) {
          setRelease(meu)
          setAberto(true)
        }
      })
      .catch(() => undefined)
    return () => {
      vivo = false
    }
  }, [podeConsultar, sistema])

  const fechar = () => {
    guardarDispensaDoDesktop(session.id)
    setAberto(false)
  }

  if (!release) return null

  const nomeDoSistema = NOME_DO_SISTEMA[sistema]

  return (
    <Modal
      open={aberto}
      onClose={fechar}
      title={`Instale o Orbind no seu ${sistema === 'linux' ? 'Linux' : nomeDoSistema}`}
      width={460}
      footer={
        <>
          <Button variant="ghost" onClick={fechar}>
            Agora não desejo instalar
          </Button>
          <Button
            variant="primary"
            icon="fas fa-download"
            onClick={() => {
              window.open(urlDeDownload(sistema), '_self')
              fechar()
            }}
          >
            Baixar para {nomeDoSistema}
          </Button>
        </>
      }
    >
      <div className="cvd">
        <div className="cvd__icone" aria-hidden>
          <i className="fas fa-desktop" />
        </div>
        <p className="cvd__texto">
          O aplicativo instalado faz coisas que o navegador não permite a uma página. Ele continua sendo o mesmo
          Orbind, com os mesmos dados — muda só onde ele roda.
        </p>
        <ul className="cvd__lista">
          <li>
            <i className="fas fa-bell" aria-hidden />
            <span>
              Avisos na bandeja do {nomeDoSistema} quando alguém te atribui um Item de Trabalho ou manda mensagem,
              mesmo com a janela fechada.
            </span>
          </li>
          <li>
            <i className="fas fa-th-large" aria-hidden />
            <span>Abre as aplicações que você cadastrou direto na área de trabalho.</span>
          </li>
          <li>
            <i className="fas fa-circle" aria-hidden />
            <span>
              Seus colegas veem que você está trabalhando mesmo com o Orbind fora de vista, em vez de aparecer como
              ausente.
            </span>
          </li>
        </ul>
        <p className="cvd__versao">
          Versão {release.version} · {(release.sizeBytes / 1024 / 1024).toFixed(1)} MB · a instalação é rápida e você
          continua logado.
        </p>
      </div>
    </Modal>
  )
}
